// calculators/true-cost/src/calculator.ts
// UI logic for the public True Cost Calculator.
// Reads inputs, validates them, runs the shared daily-compounded
// simulatePayoff() engine (the same core as the app's Plan tab and the other
// three public calculators), renders results, and fires GA4 events.
//
// All user-facing strings are looked up via `t(key)` (window.t / i18next).
// Hardcoded English fallbacks live in index.html as data-i18n attributes;
// any string this file needs at runtime comes through `t()`.

import {
  simulatePayoff,
  monthlyRateFromDailyCompounding,
} from "../../../../shared/payoffSimulation";
import { renderChart, clearChart } from "./chart";

type FieldName = "amount" | "apr" | "monthly";

interface CalculatorInputs {
  amount: number;
  apr: number;       // decimal for the engine, e.g. 0.2499 (user input is always a percentage; parseApr divides by 100)
  monthly: number;
}

interface ValidationResult {
  valid: boolean;
  values?: CalculatorInputs;
  errors: Partial<Record<FieldName, string>>;
}

// Result shape consumed by the renderers and the chart. Mirrors the fields
// the retired shared/projectPayoff ProjectionResult exposed, now derived from
// the daily-compounded shared/payoffSimulation core so the calculator's
// months / interest / true-cost figures use the same compounding convention
// as the app and the published articles (APR/365, 30-day months).
interface CalcProjection {
  monthsToPayoff: number;
  projectedTotalInterest: number;
  projectedTrueCost: number;
  projectedTrueCostMultiplier: number;
  /** Per-month series in the shape chart.ts consumes. */
  monthlyBreakdown: {
    month: number;
    payment: number;
    interestPortion: number;
    principalPortion: number;
    remainingBalance: number;
  }[];
  /**
   * `true` when the balance is not paid off within the projection horizon
   * (payment too small to cover interest, or horizon exceeded). The UI MUST
   * gate its "paid off" panel on this flag.
   */
  neverPaysOff: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────
const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;
const MAX_AMOUNT = 100000;
const MAX_APR_PCT = 50;
/** Projection horizon: 360 months (30 years) — same cap as the retired core. */
const MAX_MONTHS = 360;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ── Simulation input mapper ──────────────────────────────────────────────
// Maps the calculator's inputs onto shared/payoffSimulation's simulatePayoff
// and its SimulationResult back onto the shape the renderers/chart consume.
export function projectTrueCost(values: CalculatorInputs): CalcProjection {
  const sim = simulatePayoff({
    balance: values.amount,
    apr: values.apr,
    monthlyPayment: values.monthly,
    months: MAX_MONTHS,
  });

  const monthlyBreakdown: CalcProjection["monthlyBreakdown"] = sim.months.map(
    (m) => ({
      month: m.month,
      payment: m.paymentApplied,
      interestPortion: m.interestAccrued,
      principalPortion: round2(m.paymentApplied - m.interestAccrued),
      remainingBalance: m.closingBalance,
    })
  );

  const projectedTrueCost = round2(values.amount + sim.totalInterest);

  return {
    monthsToPayoff: sim.monthsToPayoff ?? sim.months.length,
    projectedTotalInterest: sim.totalInterest,
    projectedTrueCost,
    projectedTrueCostMultiplier:
      values.amount > 0
        ? Math.round((projectedTrueCost / values.amount) * 10000) / 10000
        : 1,
    monthlyBreakdown,
    neverPaysOff: !sim.isPaidOff,
  };
}

// ── DOM helpers ──────────────────────────────────────────────────────────
function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  // When translations are missing the key entirely, i18next.t() echoes the key
  // back. That would surface raw "calculators.trueCost.foo" text in the UI on
  // launch (Linguist hasn't added these keys yet). We pass `defaultValue` so
  // i18next falls back to English wording while still interpolating params.
  const w = window as unknown as {
    i18next?: {
      t: (k: string, p?: Record<string, string | number>) => string;
    };
  };
  if (w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  // No i18next — render the fallback. Best-effort {{token}} substitution for
  // dev/test environments where the i18n stack hasn't loaded.
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

// ── Formatting ───────────────────────────────────────────────────────────
function formatCurrency(n: number): string {
  // Use USD formatting for the public calculator MVP. Other locales fall
  // through to en formatting until per-locale formatting is wired.
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function formatMultiplier(n: number): string {
  return `${n.toFixed(2)}x`;
}

// ── Validation ───────────────────────────────────────────────────────────
function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function parseApr(raw: string): number | null {
  // Input is ALWAYS a percentage per UI contract (label "APR (%)", visible %
  // suffix, placeholder "24.99", max="50"). Convert to decimal for the engine.
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

function validate(
  amountRaw: string,
  aprRaw: string,
  monthlyRaw: string
): ValidationResult {
  const errors: Partial<Record<FieldName, string>> = {};

  const amount = parseAmount(amountRaw);
  if (amount === null || amount <= 0) {
    errors.amount = t(
      "calculators.trueCost.error_amount_required",
      "Enter a purchase amount greater than 0."
    );
  } else if (amount > MAX_AMOUNT) {
    errors.amount = t(
      "calculators.trueCost.error_amount_too_large",
      "Purchase amount must be $100,000 or less."
    );
  }

  const apr = parseApr(aprRaw);
  if (apr === null || apr <= 0) {
    errors.apr = t(
      "calculators.trueCost.error_apr_required",
      "Enter an APR greater than 0."
    );
  } else if (apr * 100 > MAX_APR_PCT) {
    errors.apr = t(
      "calculators.trueCost.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  const monthly = parseAmount(monthlyRaw);
  if (monthly === null || monthly <= 0) {
    errors.monthly = t(
      "calculators.trueCost.error_monthly_required",
      "Enter a monthly payment greater than 0."
    );
  }

  if (
    Object.keys(errors).length === 0 &&
    amount !== null &&
    apr !== null &&
    monthly !== null
  ) {
    return { valid: true, values: { amount, apr, monthly }, errors };
  }
  return { valid: false, errors };
}

// ── Buckets for GA4 (no raw values) ──────────────────────────────────────
function aprBucket(apr: number): string {
  const pct = apr * 100;
  if (pct < 10) return "<10";
  if (pct < 20) return "10-20";
  if (pct < 30) return "20-30";
  return "30+";
}

function amountBucket(amount: number): string {
  if (amount < 100) return "<100";
  if (amount < 500) return "100-500";
  if (amount < 2000) return "500-2000";
  return "2000+";
}

function multiplierBucket(m: number): string {
  if (m < 1.1) return "<1.1";
  if (m < 1.5) return "1.1-1.5";
  if (m < 2) return "1.5-2";
  return "2+";
}

// ── Field error rendering ────────────────────────────────────────────────
function renderFieldError(field: FieldName, message: string | null): void {
  const errEl = $(`tc-error-${field}`);
  const inputEl = $(`tc-input-${field}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

// ── Result rendering ─────────────────────────────────────────────────────
function showResults(): void {
  // CSS sets `.tc-results { display: none }` by default; setting style.display
  // to "" only removes the inline override, leaving the CSS rule in force.
  // Use "block" to win the cascade with an inline style.
  const r = $("tc-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "none";
  clearChart();
}

function renderNeverPaysOff(values: CalculatorInputs): void {
  // Minimum monthly to cover the first month's interest under daily
  // compounding, plus a $1 cushion to ensure principal actually moves.
  // Engine still treats payment-equal-to-interest as never-pays-off; we err
  // on the side of "definitely makes progress".
  const monthlyInterest =
    values.amount * monthlyRateFromDailyCompounding(values.apr);
  const minimumPayment = Math.ceil(monthlyInterest + 1);

  // Hide the positive result panel
  const positive = $("tc-positive-results");
  if (positive) positive.style.display = "none";

  // Show the warning panel (CSS sets `.tc-never-pays-off { display: none }`
  // by default; need explicit "block" to override the cascade)
  const warning = $("tc-never-pays-off");
  if (warning) {
    warning.style.display = "block";
    const minEl = $("tc-never-pays-off-minimum");
    if (minEl) {
      // Use innerHTML so {{minimum}} can be styled <strong> within the value.
      minEl.innerHTML = t(
        "calculators.trueCost.never_pays_off_body",
        "Your monthly payment is too small to ever pay off this balance. <strong>Minimum required: {{minimum}}/month.</strong>",
        { minimum: formatCurrency(minimumPayment) }
      );
    }
  }

  clearChart();
  gtag("event", "calc_never_pays_off_shown", { calculator: "true_cost", placement: "calculator", lang: getLang() });
}

function renderPositiveResults(
  values: CalculatorInputs,
  result: CalcProjection
): void {
  // Hide the warning panel
  const warning = $("tc-never-pays-off");
  if (warning) warning.style.display = "none";

  // Show the positive panel
  const positive = $("tc-positive-results");
  if (positive) positive.style.display = "";

  const heroLine = $("tc-result-hero");
  if (heroLine) {
    heroLine.innerHTML = t(
      "calculators.trueCost.result_actually_costs",
      "Your {{amount}} purchase actually costs {{trueCost}}",
      {
        amount: formatCurrency(values.amount),
        trueCost: `<strong>${formatCurrency(result.projectedTrueCost)}</strong>`,
      }
    );
  }

  const multEl = $("tc-result-multiplier");
  if (multEl) {
    multEl.innerHTML =
      `<strong>${formatMultiplier(result.projectedTrueCostMultiplier)}</strong> ` +
      t(
        "calculators.trueCost.result_multiplier_label",
        "true cost multiplier"
      );
  }

  const interestEl = $("tc-result-interest");
  if (interestEl) {
    interestEl.textContent =
      t(
        "calculators.trueCost.result_total_interest_label",
        "Total interest"
      ) +
      ": " +
      formatCurrency(result.projectedTotalInterest);
  }

  const monthsEl = $("tc-result-months");
  if (monthsEl) {
    monthsEl.textContent =
      t("calculators.trueCost.result_months_label", "Months to pay off") +
      ": " +
      String(result.monthsToPayoff);
  }

  // Chart
  const svg = $("tc-chart") as unknown as SVGSVGElement | null;
  if (svg) renderChart(svg, result.monthlyBreakdown);

  gtag("event", "calc_result_shown", {
    calculator: "true_cost",
    placement: "calculator",
    apr_bucket: aprBucket(values.apr),
    amount_bucket: amountBucket(values.amount),
    multiplier_bucket: multiplierBucket(result.projectedTrueCostMultiplier),
    lang: getLang(),
  });
}

// ── Run / debounce ───────────────────────────────────────────────────────
let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: FieldName | null = null;

function readInputs(): { amount: string; apr: string; monthly: string } {
  return {
    amount: ($("tc-input-amount") as HTMLInputElement | null)?.value || "",
    apr: ($("tc-input-apr") as HTMLInputElement | null)?.value || "",
    monthly: ($("tc-input-monthly") as HTMLInputElement | null)?.value || "",
  };
}

function run(): void {
  const raw = readInputs();
  const v = validate(raw.amount, raw.apr, raw.monthly);

  // Always clear all field errors on rerun, then rewrite from validation
  (["amount", "apr", "monthly"] as FieldName[]).forEach((f) =>
    renderFieldError(f, v.errors[f] || null)
  );

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    const result = projectTrueCost(v.values);

    showResults();

    if (result.neverPaysOff) {
      renderNeverPaysOff(v.values);
    } else {
      renderPositiveResults(v.values, result);
    }
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("tc-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.trueCost.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      // CSS hides .tc-runtime-error by default — explicit "block" needed
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: "true_cost", placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: "true_cost",
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

// ── Public API ───────────────────────────────────────────────────────────
export function attachCalculator(): void {
  const fields: FieldName[] = ["amount", "apr", "monthly"];
  fields.forEach((field) => {
    const el = $(`tc-input-${field}`) as HTMLInputElement | null;
    if (!el) return;
    el.addEventListener("input", () => {
      lastChangedField = field;
      // Clear runtime error on any new input
      const errBox = $("tc-runtime-error");
      if (errBox) errBox.style.display = "none";
      scheduleRecalc();
      scheduleGaInputEvent();
    });
  });

  // Wire CTA click tracking
  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep the existing event (it has GA history). The unified
      // `app_store_click` event is now owned solely by /js/store-tracking.js
      // (the global delegated listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: "true_cost", placement: "calculator", store, lang });
    });
  });

  // Direct button click handler — defense against any case where the
  // submit listener doesn't attach (e.g., i18nReady hangs).
  const calcButton = $("tc-calculate-button") as HTMLButtonElement | null;
  if (calcButton) {
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  }

  // Submit-form prevention (we recalc live; submit shouldn't navigate).
  // Also guards against Enter-key submission when a number input is focused.
  const form = $("tc-form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });
  }

  // Page-view event
  gtag("event", "calc_view", { calculator: "true_cost", placement: "calculator", lang: getLang() });

  // Initial render: if inputs are prefilled, run once.
  const raw = readInputs();
  if (raw.amount || raw.apr || raw.monthly) {
    run();
  }
}
